import React, { useEffect, useState, useCallback } from "react";
import API from "../api";
import Layout from "../components/Layout";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";


const Orders = () => {
    const navigate = useNavigate();

    const [orders, setOrders] = useState([]);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [totalPages, setTotalPages] = useState(1);
    const [search, setSearch] = useState("");

    // Filters
    const [timeFilter, setTimeFilter] = useState("All Times");
    const [showFilterPopup, setShowFilterPopup] = useState(false);
    const [filterName, setFilterName] = useState("");
    const [filterPhone, setFilterPhone] = useState("");


    const [dueFilter, setDueFilter] = useState("all");

    // FETCH ORDERS
    const fetchOrders = useCallback(async () => {
        try {
            const res = await API.get("/orders", {
                params: {
                    page,
                    limit,
                    duration: timeFilter,
                    search,
                    showDue: dueFilter
                }
            });

            let data = res.data.data;

            // Name filter
            if (filterName) {
                data = data.filter(o =>
                    o.customerName.toLowerCase().includes(filterName.toLowerCase())
                );
            }

            // Phone filter
            if (filterPhone) {
                data = data.filter(o =>
                    o.phone.includes(filterPhone)
                );
            }

            setOrders(data);
            setTotalPages(res.data.totalPages);

        } catch {
            toast.error("Failed to load orders");
        }
    }, [page, limit, search, timeFilter, filterName, filterPhone, dueFilter]);

    // AUTO FETCH (Debounce)
    useEffect(() => {
        const delay = setTimeout(() => {
            fetchOrders();
        }, 400);

        return () => clearTimeout(delay);
    }, [fetchOrders]);


    // FILTER DROPDOWN
    const handleDropdownChange = (value) => {
        setTimeFilter(value);
        setPage(1);

        if (value === "All Times") {
            setFilterName("");
            setFilterPhone("");
        } else {
            setShowFilterPopup(true);
        }
    };

    // APPLY FILTER
    const handleFilterSubmit = () => {
        setPage(1);
        setShowFilterPopup(false);
    };

    return (
        <Layout>
            <h2>Sales Records</h2>

            {/* CONTROLS */}
            <div className="table-section">
                <div className="table-controls">

                    {/* LEFT */}
                    <div className="left-controls">
                        <span>Show</span>
                        <select
                            value={limit}
                            onChange={(e) => {
                                setLimit(Number(e.target.value));
                                setPage(1);
                            }}
                        >
                            <option value={5}>5</option>
                            <option value={10}>10</option>
                            <option value={20}>20</option>
                        </select>
                        <span>entries</span>
                    </div>

                    {/* RIGHT */}
                    <div className="right-controls">

                        <select
                            value={dueFilter}
                            onChange={(e) => {
                                setDueFilter(e.target.value);
                                setPage(1);
                            }}
                        >
                            <option value="all">
                                All Due Bills
                            </option>

                            <option value="active">
                                Due Option Active
                            </option>

                            <option value="inactive">
                                Due Option Not Active
                            </option>
                        </select>

                        <input
                            type="text"
                            placeholder="Search order / customer / phone..."
                            value={search}
                            onChange={(e) => {
                                setSearch(e.target.value);
                                setPage(1);
                            }}
                        />

                        <select
                            value={timeFilter}
                            onChange={(e) =>
                                handleDropdownChange(e.target.value)
                            }
                        >
                            <option>All Times</option>
                            <option>3 Months</option>
                            <option>6 Months</option>
                            <option>12 Months</option>
                        </select>
                    </div>
                </div>

                {/* TABLE */}
                <div className="table-responsive">
                    <table className="order-table">
                        <thead>
                            <tr>
                                <th>S/N</th>
                                <th>ORDER ID</th>
                                <th>CUSTOMER</th>
                                <th>PHONE</th>
                                <th>DATE</th>
                                <th>TOTAL</th>
                                <th>PAYABLE</th>
                                <th>PAID</th>
                                <th>DUE</th>
                                <th>ACTION</th>
                            </tr>
                        </thead>

                        <tbody>
                            {orders.map((o, index) => {

                                return (
                                    <tr key={o._id}>
                                        <td>{(page - 1) * limit + index + 1}</td>
                                        <td>{o.orderId}</td>
                                        <td>{o.customerName}</td>
                                        <td>{o.phone}</td>

                                        <td>
                                            {new Date(o.createdAt).toLocaleDateString("en-IN")}
                                        </td>

                                        <td>₹{o.total}</td>

                                        <td>₹{o.payable}</td>
                                        <td>₹{o.paid}</td>

                                        <td style={{ color: o.due > 0 ? "red" : "green" }}>
                                            ₹{o.due}
                                        </td>

                                        <td>
                                            <div className="action-buttons">

                                                <button
                                                    className="pay-btn"
                                                    disabled={o.showDue !== "active" || o.due <= 0 || !o.isLatestDue}
                                                    onClick={async () => {
                                                        try {
                                                            const res = await API.get(`/orders/${o._id}`);
                                                            navigate("/pos", { state: { orderData: res.data } });
                                                        } catch (err) {
                                                            toast.error("Failed to load bill");
                                                        }
                                                    }}
                                                >
                                                    {o.showDue !== "active" ? "Final" : (o.due > 0 ? (o.isLatestDue ? "Pay" : "See Latest") : "Paid")}
                                                </button>

                                                <button
                                                    className="view-btn"
                                                    onClick={() =>
                                                        window.open(`/print-invoice/${o._id}`, "_blank")
                                                    }
                                                >
                                                    View
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                {/* PAGINATION */}
                <div className="pagination">
                        <button
                            disabled={page === 1}
                            onClick={() => setPage(page - 1)}
                        >
                            Prev
                        </button>

                        {[...Array(totalPages)].map((_, i) => (
                            <button
                                key={i}
                                className={page === i + 1 ? "active" : ""}
                                onClick={() => setPage(i + 1)}
                            >
                                {i + 1}
                            </button>
                        ))}

                        <button
                            disabled={page === totalPages}
                            onClick={() => setPage(page + 1)}
                        >
                            Next
                        </button>
                </div>
            </div>

            {/* FILTER POPUP */}
            {showFilterPopup && (
                <div className="modal-overlay">
                    <div className="modal-box">
                        <h3>{timeFilter} Sales Records</h3>

                        <input
                            placeholder="Customer Name"
                            value={filterName}
                            onChange={(e) => setFilterName(e.target.value)}
                        />

                        <input
                            placeholder="Phone"
                            value={filterPhone}
                            onChange={(e) => setFilterPhone(e.target.value)}
                        />

                        <div className="modal-footer">
                            <button onClick={() => setShowFilterPopup(false)}>
                                Cancel
                            </button>

                            <button
                                onClick={handleFilterSubmit}
                                disabled={!filterName && !filterPhone}
                            >
                                Apply
                            </button>
                        </div>
                    </div>
                </div>
            )}

        </Layout>
    );
};

export default Orders;